import { BigNumber } from 'ethers'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Colors, FontWeights } from '../../constants'
import { ExchangeQuote } from '../../hooks'
import { Currency, formatCurrency } from '../../model'
import { bigNumber18DecimalsToDollars, bigNumber18DecimalsToPercentWithPrecision } from '../../utils'
import { ExchangeImage } from './ExchangeImage'
import { SavingsBar } from './SavingsBar'

interface Props {
  currencyIn: Currency
  currencyOut: Currency
  quote: ExchangeQuote
  rank: number
  maxGasUsd: BigNumber
  bestQuote?: ExchangeQuote
  worstQuote?: ExchangeQuote
}

const GREEN = '#0c4e3e'
const RED = '#5a1829'
const EVEN = '#2f323a'
const ODD = 'transparent'

export function Exchange({ currencyIn, currencyOut, quote, rank, maxGasUsd, bestQuote, worstQuote }: Props) {
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const { amountIn, amountOut, gas, gasUsd, savings, savingsUsd, cost, costUsd, price, difference } = quote.quote
  const savingsUsdIncludingGas = savingsUsd.add(maxGasUsd).sub(gasUsd)

  const isIntegral = quote.exchange.name === 'Integral'

  let highlight: string
  if (isIntegral) {
    if (rank === 0) {
      highlight = GREEN
    } else {
      highlight = RED
    }
  } else if (rank % 2 === 0) {
    highlight = EVEN
  } else {
    highlight = ODD
  }

  let differenceDisplay
  if (rank === 0) {
    differenceDisplay = '🏆 Best Price'
  } else {
    differenceDisplay = (
      <>
        {difference.isZero() ? '' : amountIn ? '+' : '-'}
        {bigNumber18DecimalsToDollars(costUsd)}
        <small>{bigNumber18DecimalsToPercentWithPrecision(difference, 4)}</small>
      </>
    )
  }

  const tooltips = []
  if (worstQuote) {
    tooltips.push(
      <SavingsTooltip key={'savings'}>
        <strong>Savings vs {worstQuote.exchange.name}</strong>
        <span>
          {savings.value.isZero() ? '' : '+'}
          {savings.formatWithSymbol()}
        </span>
        <span>
          {savingsUsdIncludingGas.isZero() ? '' : '+'}
          {bigNumber18DecimalsToDollars(savingsUsdIncludingGas)} incl. gas
        </span>
      </SavingsTooltip>
    )
  }
  if (bestQuote) {
    tooltips.push(
      <CostsTooltip key={'costs'}>
        <strong>Costs vs {bestQuote.exchange.name}</strong>
        <span>
          {cost.value.isZero() ? '' : '-'}
          {cost.formatWithSymbol()}
        </span>
        <span>
          {costUsd.isZero() ? '' : '-'}
          {bigNumber18DecimalsToDollars(costUsd)}
        </span>
      </CostsTooltip>
    )
  }
  tooltips.push(
    <GasTooltip key={'gas'}>
      <strong>Estimated Gas</strong>
      <span>{formatCurrency(0, gas)} Gas</span>
      <span>{bigNumber18DecimalsToDollars(gasUsd)}</span>
    </GasTooltip>
  )

  return (
    <Container onMouseEnter={() => setTooltipVisible(true)} onMouseLeave={() => setTooltipVisible(false)}>
      <InfoRow highlight={highlight} rank={rank} isIntegral={isIntegral}>
        <td rowSpan={2}>
          <ExchangeImage exchange={quote.exchange.name} />
        </td>
        <td>{quote.exchange.name}</td>
        <td>
          {amountIn?.formatWithSymbol() || amountOut?.formatWithSymbol()}
          <small>{`1 ${currencyOut.symbol} = ${formatCurrency(18, price)} ${currencyIn.symbol}`}</small>
        </td>
        <td>{differenceDisplay}</td>
      </InfoRow>
      <SavingsBarRow highlight={highlight}>
        <td colSpan={3}>
          <SavingsBar quote={quote} maxGasUsd={maxGasUsd} />
          {tooltipVisible && <Tooltip>{tooltips}</Tooltip>}
        </td>
      </SavingsBarRow>
    </Container>
  )
}

const Container = styled.tbody``

interface HighlightProps {
  highlight: string
}

const InfoRow = styled.tr.attrs((props: HighlightProps & { rank: number; isIntegral: boolean }) => ({
  highlight: props.highlight,
  rank: props.rank,
  isIntegral: props.isIntegral,
}))`
  td {
    padding: 10px 10px 0 10px;
    vertical-align: top;
    text-align: left;
    font-weight: ${(props) => (props.isIntegral ? FontWeights.Bold : FontWeights.Regular)};
    background-color: ${(props) => props.highlight};

    &:first-child {
      padding: 15px 0 0 20px;
      width: 71px;
    }

    &:nth-child(2) {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 20px;
      text-align: right;
      color: ${(props) => (props.rank === 0 ? 'inherit' : Colors.Red)};
    }

    small {
      display: block;
      color: #a8a8a8;
      font-size: 11px;
    }

    @media (max-width: 1024px) {
      font-size: 12px;

      &:first-child {
        padding-left: 15px;
        width: 61px;
      }

      small {
        padding: 5px 0;
        font-size: 10px;
      }
    }
  }
`

const SavingsBarRow = styled.tr.attrs((props: HighlightProps) => ({
  highlight: props.highlight,
}))`
  td {
    padding: 5px 20px 10px 0;
    vertical-align: top;
    background-color: ${(props) => props.highlight};
  }
`

const Tooltip = styled.ul`
  margin: 10px 0 0 0;
  padding: 0;
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  list-style: none;

  strong {
    margin-bottom: 5px;
    display: block;
    font-size: 11px;
    text-transform: uppercase;
  }

  span {
    margin-top: 3px;
    display: block;
  }
`

const SavingsTooltip = styled.li`
  span {
    color: ${Colors.Green};
  }
`

const CostsTooltip = styled.li`
  span {
    color: ${Colors.Red};
  }
`

const GasTooltip = styled.li`
  span {
    color: ${Colors.Gray};
  }
`
